import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";

export default function RecordsTable({ hideHeading = false, title = "Catch release records", excelFile, downloadFileName = "records.csv" }) {
  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [filterText, setFilterText] = useState("");

  useEffect(() => {
    if (!excelFile) {
      setError("No Excel source specified.");
      setLoading(false);
      return;
    }

    fetch(excelFile)
      .then((res) => res.arrayBuffer())
      .then((buffer) => {
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        if (json.length === 0) {
          setError("Excel sheet has no data.");
        } else {
          setCols(Object.keys(json[0]));
          setRows(json);
        }
      })
      .catch((err) => setError(err.message || "Failed to load Excel file."))
      .finally(() => setLoading(false));
  }, [excelFile]);

  const filteredRows = useMemo(() => {
    let filtered = rows;
    if (filterText.trim()) {
      const lower = filterText.trim().toLowerCase();
      filtered = filtered.filter((row) =>
        cols.some((col) => String(row[col] || "").toLowerCase().includes(lower))
      );
    }

    if (sortBy) {
      const sorted = [...filtered].sort((a, b) => {
        const aValue = String(a[sortBy] ?? "");
        const bValue = String(b[sortBy] ?? "");
        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
      return sorted;
    }

    return filtered;
  }, [rows, cols, filterText, sortBy, sortDirection]);

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDirection("asc");
    }
  };

  const downloadCsv = () => {
    if (!cols.length || !rows.length) return;

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", downloadFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-6">
      {!hideHeading && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-2xl font-semibold text-white">{title}</h2>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={downloadCsv}
              className="rounded-lg border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-400/20"
            >
              Download CSV
            </button>
            <input
              type="text"
              placeholder="Search records..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="rounded-lg border border-white/20 bg-slate-900/80 px-3 py-2 text-sm text-white outline-none focus:border-cyan-300"
            />
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-cyan-200">Loading records…</p>
      ) : error ? (
        <p className="text-red-300">Error: {error}</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10 bg-slate-900/70 p-2">
          <table className="min-w-full table-auto text-left text-sm text-slate-100">
            <thead>
              <tr className="bg-slate-800/80">
                {cols.map((col) => (
                  <th
                    key={col}
                    className="cursor-pointer border border-white/10 px-3 py-2 font-medium"
                    onClick={() => toggleSort(col)}
                  >
                    {col}
                    {sortBy === col ? (sortDirection === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.length ? (
                filteredRows.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 0 ? "bg-slate-950/40" : "bg-slate-900/30"}>
                    {cols.map((col) => (
                      <td key={col} className="border border-white/10 px-3 py-2 align-top text-slate-200">
                        {row[col] ?? ""}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={cols.length} className="px-3 py-4 text-center text-slate-300">
                    No rows match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
